import { useEffect, useRef } from 'react';

type UseSequenceOptions = {
  keyboardSequence?: string[];
  touchSequence?: string[];
  handleAction: () => void;
  timeoutMs?: number;
  swipeThreshold?: number;
};

export function useSequence({
  keyboardSequence = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'c', 'c'],
  touchSequence = [
    'swipe-up',
    'swipe-up',
    'swipe-down',
    'swipe-down',
    'tap',
    'tap',
  ],
  handleAction,
  timeoutMs = 5000,
  swipeThreshold = 50,
}: UseSequenceOptions) {
  const positionRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const touchStartRef = useRef({ x: 0, y: 0 });

  const resetProgress = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);

    // reset timeout to clear progress after inactivity
    timerRef.current = window.setTimeout(() => {
      positionRef.current = 0;
    }, timeoutMs);
  };

  const checkSequence = (input: string, sequence: string[]) => {
    if (input === sequence[positionRef.current]) {
      positionRef.current += 1;
      if (positionRef.current === sequence.length) {
        handleAction();
        positionRef.current = 0;
        return;
      }
    }

    // reset if key doesn't match current position
    else positionRef.current = 0;
  };

  useEffect(() => {
    // keyboard
    const handleKeyDown = (e: KeyboardEvent) => {
      resetProgress();
      checkSequence(e.key, keyboardSequence);
    };

    // touch
    const handleTouchStart = (e: TouchEvent) => {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };

    const handleTouchEnd = (e: TouchEvent) => {
      resetProgress();

      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const deltaX = endX - touchStartRef.current.x;
      const deltaY = endY - touchStartRef.current.y;

      let gesture: string | null = null;

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        if (deltaY < -swipeThreshold) gesture = 'swipe-up';
        else if (deltaY > swipeThreshold) gesture = 'swipe-down';
      } else {
        if (deltaX < -swipeThreshold) gesture = 'swipe-left';
        else if (deltaX > swipeThreshold) gesture = 'swipe-right';
      }
      if (!gesture && Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10)
        gesture = 'tap';

      if (gesture) checkSequence(gesture, touchSequence);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyboardSequence,
    touchSequence,
    handleAction,
    timeoutMs,
    swipeThreshold,
  ]);
}
